import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { getPortfolio } from '../api/portfolio';
import {
  getAllPositions,
  getPositionSummary,
  type PositionSummary,
} from '../api/position';
import {
  BLOTTER_STATUSES,
  getExternalTrades,
  type ExternalTradeOrder,
  type ExternalTradeResponse,
} from '../api/external-trade';
import type { ScopeContext } from '../../auth/model/types';

// The page's one statement of this policy — other queries reading the same portfolio
// (`use-portfolio-valuation.ts`) import this rather than restating the literal.
export const PORTFOLIO_DETAIL_STALE_MS = 30_000;

// The pre-caching page made exactly one attempt per read and reported a failure the
// moment it landed — the degraded-state UI (and the tests that lock it in) are built on
// that promise: a retry button appears, and reaching for it is the user's decision, not
// a delay TanStack's default 3-attempt backoff inserts before the failure is even shown.
// Background refetching is off for the same reason — regaining a window or a network
// connection must not silently start a request the user did not ask for; every refetch
// here is either the initial mount, a key change, or an explicit retry/refresh action.
// Exported as the page's one statement of that policy — the import pipeline's queries
// make the same promise and import these rather than restating them.
export const PORTFOLIO_DETAIL_RETRY = false;
export const PORTFOLIO_DETAIL_REFETCH_OPTIONS = {
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} as const;

/**
 * One builder per query so the key used by `useQuery` and the prefix used by
 * `invalidateQueries` can never drift apart (see knowledge/reviewing.md — a
 * value composed in more than one place needs one named builder).
 */
export const portfolioDetailQueryKeys = {
  portfolio: (portfolioId: string) => ['portfolio-detail', portfolioId] as const,
  positions: (portfolioId: string) => ['portfolio-positions', portfolioId] as const,
  summaryByPortfolio: (portfolioId: string) => ['portfolio-position-summary', portfolioId] as const,
  summary: (portfolioId: string, currency: string) =>
    [...portfolioDetailQueryKeys.summaryByPortfolio(portfolioId), currency] as const,
  externalTradesByPortfolio: (portfolioId: string) => ['portfolio-external-trades', portfolioId] as const,
  externalTrades: (portfolioId: string, orderBy: ExternalTradeOrder, showsCanceled: boolean) =>
    [...portfolioDetailQueryKeys.externalTradesByPortfolio(portfolioId), orderBy, showsCanceled] as const,
  booking: (portfolioId: string) => ['portfolio-booking', portfolioId] as const,
  navHistoryByPortfolio: (portfolioId: string) => ['portfolio-nav-history', portfolioId] as const,
  navHistory: (portfolioId: string, period: string, currency: string) =>
    [...portfolioDetailQueryKeys.navHistoryByPortfolio(portfolioId), period, currency] as const,
};

/**
 * A `placeholderData` function that carries the previous key's data forward only when
 * that previous key belonged to the *same* portfolio. Every query key here puts the
 * portfolio id at index 1 (see the builders above), so comparing that one segment is
 * enough. Without this check, TanStack's own `keepPreviousData` would show portfolio A's
 * figures for a beat while portfolio B's very first fetch is still in flight — a
 * cross-portfolio leak the plain helper cannot tell apart from an ordinary key change.
 */
function samePortfolioOnly<T>(portfolioId: string) {
  return (previousData: T | undefined, previousQuery?: { queryKey: readonly unknown[] }) =>
    previousQuery?.queryKey[1] === portfolioId ? previousData : undefined;
}

export function usePortfolioQuery(
  sessionId: string | null,
  accountScope: ScopeContext | null,
  portfolioId: string | undefined,
) {
  return useQuery({
    queryKey: portfolioDetailQueryKeys.portfolio(portfolioId ?? ''),
    queryFn: () => getPortfolio(sessionId!, accountScope!, portfolioId!),
    enabled: !!sessionId && !!accountScope && !!portfolioId,
    staleTime: PORTFOLIO_DETAIL_STALE_MS,
    retry: PORTFOLIO_DETAIL_RETRY,
    ...PORTFOLIO_DETAIL_REFETCH_OPTIONS,
  });
}

/**
 * One query per portfolio covering every page of positions, not the first page alone —
 * `getAllPositions` walks the cursor inside the query function. So `isLoading` stays true
 * until the last page lands, which is what keeps the table from ranking a partial set,
 * and one cache entry holds the whole portfolio. Re-sorting reads that entry and never
 * refetches: the sort is component state in `PositionsTable`, not part of this key.
 *
 * That walk is up to 51 signed requests, so the abort signal TanStack hands every query
 * function is passed down and checked between pages: leaving the screen, or opening
 * another portfolio, stops the walk after the page already in flight instead of firing
 * the rest at a screen nobody is on. The two siblings that walk a cursor
 * (`getAllPortfolios`, `getAllInstrumentsOfType`) take the same `isCancelled` callback.
 */
export function usePositionsQuery(
  sessionId: string | null,
  accountScope: ScopeContext | null,
  portfolioId: string | undefined,
) {
  return useQuery({
    queryKey: portfolioDetailQueryKeys.positions(portfolioId ?? ''),
    queryFn: ({ signal }) => getAllPositions(sessionId!, accountScope!, portfolioId!, {
      isCancelled: () => signal.aborted,
    }),
    enabled: !!sessionId && !!accountScope && !!portfolioId,
    staleTime: PORTFOLIO_DETAIL_STALE_MS,
    retry: PORTFOLIO_DETAIL_RETRY,
    ...PORTFOLIO_DETAIL_REFETCH_OPTIONS,
  });
}

/**
 * Wraps the NAV summary read with a currency-scoped key so toggling back to a currency
 * seen within the last 30s reuses the cache instead of re-fetching. The placeholder
 * carries the previous currency's figures forward while a new one loads — scoped to the
 * same portfolio, per `samePortfolioOnly` — so the NAV card never blanks mid-toggle.
 *
 * TanStack already retains the last successful `data` through a failed *background*
 * refetch of the same key. The one gap that leaves is a currency that has never
 * succeeded at all: its `data` is genuinely `undefined` on failure, nothing was ever
 * cached under that key. `lastSummary` covers exactly that gap — the last summary that
 * ever succeeded, for this portfolio — so a failed switch to a new currency still has
 * something to fall back to. It is kept as `{portfolioId, summary}` and ignored on a
 * mismatch, for the same cross-portfolio reason `samePortfolioOnly` exists.
 *
 * Only the fields `detail.tsx` actually reads are returned, rather than the whole
 * tracked query result.
 */
export function usePositionSummaryQuery(
  sessionId: string | null,
  accountScope: ScopeContext | null,
  portfolioId: string | undefined,
  currency: string | null,
) {
  const query = useQuery({
    queryKey: portfolioDetailQueryKeys.summary(portfolioId ?? '', currency ?? ''),
    queryFn: () => getPositionSummary(sessionId!, accountScope!, portfolioId!, currency!),
    enabled: !!sessionId && !!accountScope && !!portfolioId && currency !== null,
    staleTime: PORTFOLIO_DETAIL_STALE_MS,
    retry: PORTFOLIO_DETAIL_RETRY,
    placeholderData: samePortfolioOnly<PositionSummary>(portfolioId ?? ''),
    ...PORTFOLIO_DETAIL_REFETCH_OPTIONS,
  });

  const [lastSummary, setLastSummary] = useState<{ portfolioId: string; summary: PositionSummary } | null>(null);
  useEffect(() => {
    if (query.data && portfolioId) setLastSummary({ portfolioId, summary: query.data });
  }, [query.data, portfolioId]);

  const stickySummary = lastSummary != null && lastSummary.portfolioId === portfolioId ? lastSummary.summary : null;

  return {
    // For display: the current currency's figures, falling back across currencies so a
    // switch that never succeeded still has the last-good one to show.
    summary: query.data ?? stickySummary,
    isError: query.isError,
    isLoading: query.isLoading || currency === null,
    isFetching: query.isFetching,
    refetch: query.refetch,
  };
}

/**
 * Mirrors `usePositionSummaryQuery`'s retention exactly, for the same reason: a re-sort
 * or a filter toggle is a brand-new order/showsCanceled key, so a failure there has no
 * `data` of its own to retain, even though the *portfolio's* rows a moment ago are still
 * perfectly good to keep showing (with `hasFailed`'s banner alongside them, the same
 * pattern `OperationsTab` already applies for a same-key refetch failure). `lastTrades`
 * covers that gap the same way `lastSummary` does — last-good rows for this portfolio,
 * regardless of which sort or filter produced them — and `samePortfolioOnly` keeps the
 * placeholder itself from leaking a different portfolio's rows across a param change.
 */
export function useExternalTradesQuery(
  sessionId: string | null,
  accountScope: ScopeContext | null,
  portfolioId: string | undefined,
  orderBy: ExternalTradeOrder,
  showsCanceled: boolean,
) {
  const query = useQuery({
    queryKey: portfolioDetailQueryKeys.externalTrades(portfolioId ?? '', orderBy, showsCanceled),
    queryFn: () =>
      getExternalTrades(sessionId!, accountScope!, {
        portfolioId: portfolioId!,
        orderBy,
        statuses: showsCanceled ? undefined : BLOTTER_STATUSES,
      }),
    enabled: !!sessionId && !!accountScope && !!portfolioId,
    staleTime: PORTFOLIO_DETAIL_STALE_MS,
    retry: PORTFOLIO_DETAIL_RETRY,
    placeholderData: samePortfolioOnly<ExternalTradeResponse>(portfolioId ?? ''),
    ...PORTFOLIO_DETAIL_REFETCH_OPTIONS,
  });

  const [lastTrades, setLastTrades] = useState<{ portfolioId: string; response: ExternalTradeResponse } | null>(null);
  useEffect(() => {
    if (query.data && portfolioId) setLastTrades({ portfolioId, response: query.data });
  }, [query.data, portfolioId]);

  const stickyTrades = lastTrades != null && lastTrades.portfolioId === portfolioId ? lastTrades.response : null;

  return {
    data: query.data ?? stickyTrades,
    isError: query.isError,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    refetch: query.refetch,
  };
}
